import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';

export default function VendorTermsPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="p-8 prose prose-slate max-w-none">
            <h1 className="text-3xl font-bold text-foreground mb-6">
              GoCompareAI Vendor Partner Terms of Service
            </h1>
            
            <p className="text-muted-foreground text-sm mb-8">
              Last updated: February 2025 | Version 1.0
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                These Vendor Partner Terms of Service ("Vendor Terms") govern your participation in the 
                GoCompareAI Vendor Portal as an AI platform provider ("Vendor", "you", or "your"). 
                By creating a vendor account or accessing the Vendor Portal, you agree to be bound by these terms.
              </p>
              <p className="text-muted-foreground">
                These Vendor Terms are supplemental to our general Terms of Service and Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">2. Vendor Account & Eligibility</h2>
              <p className="text-muted-foreground mb-4">
                To participate as a Vendor, you must:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Be an authorised representative of the AI platform you are claiming</li>
                <li>Provide accurate and complete information about your platform</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorised access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">3. Platform Information & Content</h2>
              <p className="text-muted-foreground mb-4">
                As a Vendor, you may edit and update factual information about your platform, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Product descriptions and summaries</li>
                <li>Features and capabilities</li>
                <li>Pricing information</li>
                <li>Data handling and privacy practices</li>
                <li>Integration options and technical specifications</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                <strong>You agree that:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All information provided will be accurate, complete, and not misleading</li>
                <li>You will not make false claims about your platform's capabilities</li>
                <li>You will update information promptly when it becomes outdated</li>
                <li>All edits are subject to review and approval by GoCompareAI</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">4. Editorial Independence</h2>
              <p className="text-muted-foreground mb-4">
                GoCompareAI maintains full editorial independence over:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>"Our Assessment" sections and analysis</li>
                <li>Suitability recommendations and matching logic</li>
                <li>Confidence indicators and data source attributions</li>
                <li>Comparison rankings and positioning</li>
                <li>Platform categorisation and tagging</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Vendors cannot influence, modify, or request changes to these independent assessments. 
                This separation ensures users receive unbiased recommendations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">5. Vendor Tiers & Features</h2>
              <p className="text-muted-foreground mb-4">
                GoCompareAI offers different vendor tiers with varying capabilities:
              </p>
              
              <div className="bg-muted/50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-foreground mb-2">Claimed (Free)</h3>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                  <li>Basic profile editing</li>
                  <li>3 edits per day limit</li>
                  <li>All edits require admin approval</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-blue-700 mb-2">Verified Partner (Paid)</h3>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                  <li>Public "Verified Partner" badge</li>
                  <li>10 edits per day</li>
                  <li>Links auto-publish without approval</li>
                  <li>Access to analytics dashboard</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-semibold text-amber-700 mb-2">Enterprise Partner (Paid)</h3>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                  <li>Public "Enterprise Partner" badge</li>
                  <li>50 edits per day</li>
                  <li>Safe fields auto-publish instantly</li>
                  <li>Full analytics and comparison insights</li>
                  <li>Priority support</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">6. Payment Terms</h2>
              <p className="text-muted-foreground mb-4">
                For paid tiers (Verified Partner and Enterprise Partner):
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Fees are billed annually in advance</li>
                <li>All fees are non-refundable unless otherwise stated</li>
                <li>Price changes will be communicated 30 days in advance</li>
                <li>Failure to pay may result in downgrade to the free tier</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">7. Prohibited Conduct</h2>
              <p className="text-muted-foreground mb-4">
                Vendors may not:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Submit false, misleading, or deceptive information</li>
                <li>Attempt to manipulate rankings or recommendations</li>
                <li>Claim platforms or products they don't represent</li>
                <li>Share account credentials with unauthorised parties</li>
                <li>Use automated systems to submit bulk edits</li>
                <li>Disparage competitors or other platforms</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">8. Suspension & Termination</h2>
              <p className="text-muted-foreground mb-4">
                GoCompareAI reserves the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Suspend or terminate vendor accounts for violations of these terms</li>
                <li>Reject or remove content that violates our policies</li>
                <li>Downgrade tier status for non-payment</li>
                <li>Remove platform listings entirely in cases of serious violations</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You may terminate your vendor account at any time by contacting us. 
                Upon termination, your platform profile will revert to publicly available information only.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">9. Data & Privacy</h2>
              <p className="text-muted-foreground mb-4">
                By using the Vendor Portal, you acknowledge that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Platform information you provide will be publicly displayed</li>
                <li>We may collect analytics on how users interact with your platform listing</li>
                <li>Your contact information is used for account management purposes</li>
                <li>We will not share your login credentials or private account data with third parties</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                For more details, please review our Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">10. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                GoCompareAI provides the Vendor Portal "as is" without warranties of any kind. 
                We are not liable for any indirect, incidental, or consequential damages arising from 
                your use of the Vendor Portal. Our total liability shall not exceed the fees paid by 
                you in the 12 months preceding any claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">11. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these Vendor Terms from time to time. Material changes will be communicated 
                via email at least 30 days before taking effect. Continued use of the Vendor Portal 
                after changes take effect constitutes acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">12. Contact</h2>
              <p className="text-muted-foreground">
                For questions about these Vendor Terms, please contact us at:
              </p>
              <p className="text-muted-foreground mt-2">
                <strong>Email:</strong> info@gocompareai.com
              </p>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground">
                By clicking "I Accept" during account activation, you confirm that you have read, 
                understood, and agree to be bound by these Vendor Partner Terms of Service.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
}
